import React, { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Button, Empty, message, Typography } from "antd";
import { debounce } from "lodash";
import {
  apiSearchWholesalesSupplier,
  apiGetMySavedWholesaleSuppliers,
  apiUpdateMySavedWholesaleSuppliers
} from "api/wholesales";
import { connectAutoDispatch } from "../../../../redux/reduxConnect";
import { compose } from "redux";

const NewestWholesaleSuppliers = ({ userState }) => {
  const {
    userInfo: { isInTrial, productType }
  } = userState;

  if (!isInTrial && !["basic", "hazmat"].includes(productType)) {
    return (
      <Empty
        description={
          <Typography.Text strong>
            Subscribe to AZInsight Advanced plan now to access the wholesale suppliers list.
          </Typography.Text>
        }
      />
    );
  }

  const gridRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [mySavedList, setMySavedList] = useState([]);
  const mySavedListRef = useRef(mySavedList);

  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "",
      field: "_id",
      hide: true
    },
    {
      headerName: "Location",
      field: "location",
      pinned: "left",
      width: 100
    },
    {
      headerName: "Category",
      field: "category",
      pinned: "left",
      width: 100
    },
    {
      headerName: "Company",
      field: "company",
      pinned: "left",
      width: 200
    },
    {
      headerName: "Address",
      field: "address",
      width: 200
    },
    {
      headerName: "Website",
      field: "site",
      width: 200,
      cellRenderer: params => {
        if (!params.value) return null;
        let url = params.value;
        try {
          url = new URL(params.value).href;
        } catch (e) {
          url = `https://${params.value}`;
        }
        return (
          <a href={url} target="_blank" rel="noreferrer">
            {params.value}
          </a>
        );
      }
    },
    {
      headerName: "Description",
      field: "description",
      width: 200
    },
    {
      headerName: "Wholesale Type",
      field: "wholesaleType",
      width: 200,
      valueFormatter: params => (params.value || []).join(", ")
    },
    {
      headerName: "Approved For",
      field: "approvedFor",
      width: 200,
      valueFormatter: params => (params.value || []).join(", ")
    },
    {
      headerName: "Brands",
      field: "brandNames",
      width: 200,
      valueFormatter: params => (params.value || []).join(", ")
    },
    {
      headerName: "Product Types",
      field: "productTypes",
      width: 200,
      valueFormatter: params => (params.value || []).join(", ")
    },
    {
      headerName: "Actions",
      field: "action",
      pinned: "right",
      width: 120,
      cellRenderer: params => {
        const supplier = params.data;
        const isSaved = supplier.saved;
        return (
          <Button onClick={() => handleOnSave(supplier._id)} disabled={isSaved} loading={loading}>
            {isSaved ? "Saved" : "Save"}
          </Button>
        );
      }
    }
  ]);

  const limit = 10;

  const getRowId = params => params.data._id;

  const [gridOptions] = useState({
    rowModelType: "serverSide",
    rowSelection: "multiple",
    defaultColDef: {
      sortable: true,
      resizable: true,
      filter: true
    },
    isExternalFilterPresent: () => true,
    doesExternalFilterPass: () => true,
    domLayout: "autoHeight",
    cacheBlockSize: limit,
    suppressHorizontalScroll: false,
    suppressContextMenu: true
  });

  const onGridReady = params => {
    gridRef.current.api.setColumnDefs(columnDefs);
    gridRef.current.api.closeToolPanel();
    onGetList();
  };

  function getServerSideDatasource() {
    return {
      getRows: debounce(params => {
        const { startRow } = params.request;
        const page =
          startRow === 0 || startRow === undefined ? 1 : Math.floor(startRow / limit) + 1;

        apiSearchWholesalesSupplier({
          page,
          limit,
          company: null,
          recent: true,
          option: {
            category: null,
            location: [],
            approvedFor: [],
            wholesaleType: [],
            productTypes: []
          }
        })
          .then(resp => {
            const suppliers = resp?.data?.suppliers || [];
            if (!Array.isArray(suppliers)) {
              params.fail();
              gridRef.current.api.showNoRowsOverlay();
              return;
            }

            const resultTotal = resp?.data?.total;

            gridRef.current.api.hideOverlay();

            const newSuppliers = suppliers.map(supplier => {
              const isSaved = mySavedListRef.current.some(
                saved => saved.wholesaleId === supplier._id
              );
              return {
                ...supplier,
                saved: isSaved
              };
            });

            params.successCallback(newSuppliers, resultTotal);
          })
          .catch(error => {
            console.error("API Error:", error);
            params.fail();
            gridRef.current.api.showNoRowsOverlay();
          });
      }, 500)
    };
  }

  const handleOnSave = supplierId => {
    setLoading(true);
    apiUpdateMySavedWholesaleSuppliers({
      id: "",
      wholesaleId: supplierId,
      myNote: ""
    })
      .then(() => {
        return fetchMySaved();
      })
      .then(() => {
        message.success("Supplier saved successfully");
        onGetList();
      })
      .catch(() => message.error("Cannot save, please try again"))
      .finally(() => setLoading(false));
  };

  const fetchMySaved = () => {
    setLoading(true);
    return apiGetMySavedWholesaleSuppliers({
      page: 1,
      limit: 100
    })
      .then(({ data }) => {
        setMySavedList(data?.suppliers || []);
      })
      .catch(() => message.error("Cannot get my saved list, please try again"))
      .finally(() => setLoading(false));
  };

  const onGetList = () => {
    const dataSource = getServerSideDatasource();
    gridRef.current.api.setServerSideDatasource(dataSource);
  };

  useEffect(() => {
    fetchMySaved();
  }, []);

  useEffect(() => {
    mySavedListRef.current = mySavedList;
  }, [mySavedList]);

  return (
    <div>
      <Typography.Title>Newest Wholesale Suppliers</Typography.Title>
      <div className="ag-theme-alpine" style={{ width: "100%", height: "500px" }}>
        <AgGridReact
          ref={gridRef}
          style={{ width: "100%", height: "100%" }}
          animateRows
          enableRangeSelection
          getRowId={getRowId}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          pagination
          paginationPageSize={limit}
        />
      </div>
    </div>
  );
};

const withConnect = connectAutoDispatch(
  ({ userState }) => ({
    userState
  }),
  {}
);

export default compose(withConnect)(NewestWholesaleSuppliers);
